import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, ArrowLeft } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { identify, track } from "@/lib/analytics";

export default function AdminLogin() {
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validate = () => {
    const newErrors = {};
    if (!email.trim()) newErrors.email = "Email is required";
    else if (!validateEmail(email)) newErrors.email = "Invalid email format";
    if (!password) newErrors.password = "Password is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.user?.role !== "admin") {
        toast.error("This account does not have admin access.");
        return;
      }
      localStorage.setItem("neivai_token", data.token);
      localStorage.setItem("neivai_user", JSON.stringify(data.user));
      setUser(data.user);
      identify(data.user.id, { email: data.user.email, role: data.user.role });
      track("login", { role: "admin" });
      toast.success(`Welcome back, ${data.user.business_name || data.user.email}`);
      nav("/admin", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === "string") {
        toast.error(detail);
      } else if (detail?.msg) {
        toast.error(detail.msg);
      } else {
        toast.error("Login failed");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-transparent px-8 py-16">
      <div className="w-full max-w-md">
        <div className="rounded-2xl bg-white p-10 shadow-xl">
          <div className="flex items-center justify-between">
            <Logo size="md" />
            <button
              onClick={() => nav(-1)}
              className="flex items-center gap-1 text-sm text-[#5C5F62] hover:text-[#1A1C1E] transition"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>
          </div>
          <div className="mt-10 flex items-center gap-3">
            <span className="h-10 w-10 rounded-full bg-[#F3F1EC] flex items-center justify-center shrink-0">
              <ShieldCheck className="h-5 w-5 text-[#C86B53]" />
            </span>
            <div>
              <h1 className="text-2xl font-display font-semibold tracking-tight">Admin Login</h1>
              <p className="text-sm text-[#5C5F62]">Platform administration</p>
            </div>
          </div>
          <form className="mt-8 space-y-5" onSubmit={onSubmit}>
            <div>
              <label className="text-sm font-medium text-[#1A1C1E]">Email</label>
              <input
                data-testid="admin-login-email-input"
                type="email"
                required
                autoFocus
                value={email}
                onChange={(e) => { setEmail(e.target.value); if (errors.email) setErrors({ ...errors, email: "" }); }}
                className={`mt-2 w-full bg-white border rounded-md px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#C86B53]/20 focus:border-[#C86B53] transition ${errors.email ? "border-[#B74F4F]" : "border-[#E5E5E5]"}`}
                placeholder="admin@neiv.ai"
              />
              {errors.email && <p className="text-xs text-[#B74F4F] mt-1">{errors.email}</p>}
            </div>
            <div>
              <label className="text-sm font-medium text-[#1A1C1E]">Password</label>
              <input
                data-testid="admin-login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => { setPassword(e.target.value); if (errors.password) setErrors({ ...errors, password: "" }); }}
                className={`mt-2 w-full bg-white border rounded-md px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#C86B53]/20 focus:border-[#C86B53] transition ${errors.password ? "border-[#B74F4F]" : "border-[#E5E5E5]"}`}
                placeholder="••••••••"
              />
              {errors.password && <p className="text-xs text-[#B74F4F] mt-1">{errors.password}</p>}
            </div>
            <div className="flex justify-end">
              <Link to="/forgot-password" className="text-xs text-[#5C5F62] hover:text-[#C86B53] transition">Forgot password?</Link>
            </div>
            <button
              data-testid="admin-login-submit-button"
              type="submit"
              disabled={busy}
              className="neivai-btn-primary w-full disabled:opacity-60"
            >
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>
        <p className="mt-6 text-center text-xs text-white/50">
          Not an admin? <Link to="/login" className="text-white/80 hover:text-white underline">Go to the main login</Link>
        </p>
      </div>
    </div>
  );
}
